import { useState } from "react";
import axiosInstance from "@/hooks/axios";
import { isAxiosError } from "axios";
import { UserEducation } from "@/types/user/user.types";

export interface UpdateUserEducationData {
  education_id: UserEducation["education"]["id"];
  institution_name: UserEducation["institution_name"];
  is_currently_studying?: UserEducation["is_currently_studying"];
  remove_education?: boolean;
}

export interface UpdateUserEducationResponse {
  success: boolean;
  message: string;
  education?: {
    id: UserEducation["id"];
    user: UserEducation["user"];
    education: UserEducation["education"];
    institution_name: UserEducation["institution_name"];
    is_currently_studying: UserEducation["is_currently_studying"];
  };
}

export const useUpdateUserEducation = () => {
  const [data, setData] = useState<UpdateUserEducationResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const updateUserEducation = async (formData: UpdateUserEducationData) => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    const authToken = localStorage.getItem("authToken");
    if (!authToken) {
      setError("Not authenticated.");
      setLoading(false);
      return;
    }

    try {
      const response = await axiosInstance.post<UpdateUserEducationResponse>(
        "users/users/update_user_education/",
        formData,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        },
      );

      setData(response.data);

      if (response.data.success) {
        setSuccess(true);
      }
    } catch (err: unknown) {
      if (isAxiosError(err)) {
        setError(err.message);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("An unknown error occurred.");
      }
    } finally {
      setLoading(false);
    }
  };

  return { data, loading, error, success, updateUserEducation };
};
